import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import SuperFetch from 'library/helpers/superFetch';
import fieldsValidator from 'library/helpers/validators/fieldsValidator';
import _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { categoriesActions } from 'reduxStore/categories/slice';
import { Notification } from 'zComponents/atoms';
import { Button, Col, Divider, Flex, Form, Input, Modal, Row, Select } from 'zui/Antd';

const { TextArea } = Input;

const EditCategoryModal = ({ children, visible, setVisible, category }) => {
	const dispatch = useDispatch();

	const {
		token: { colorTextDescription }
	} = theme.useToken();

	const { data: sitesData } = useSelector((state) => state.Sites);
	const { categoryTypes, fieldTypes } = useSelector((state) => state.Types);

	const [confirmLoading, setConfirmLoading] = useState(false);
	const [disabledEditData, setDisabledEditData] = useState(true);
	const [newAssociations, setNewAssociations] = useState([]);
	const [deleteAssociations, setDeleteAssociations] = useState([]);

	const [form] = Form.useForm();
	const oldState = useRef();

	useEffect(() => {
		const EDIT_DATA = {
			name: category.name,
			siteID: category.sites?.map((site) => site.siteID),
			typeID: category.typeID,
			description: category.description,
			fields: category.fields
		};
		oldState.current = EDIT_DATA;
		form.setFieldsValue(EDIT_DATA);
	}, [category.name, category.sites, category.typeID, category.description, category.fields, visible, form]);

	const handleCancel = () => {
		setVisible(false);
		form.resetFields();
	};

	const onChangSelect = (array) => {
		const added = [];
		const deleted = [];

		// Check for deleted properties
		category.sites.forEach((item) => {
			const found = array.find((modItem) => modItem === item.siteID);
			if (!found) {
				deleted.push(item.siteID);
			}
		});

		// Check for added properties
		array.forEach((item) => {
			const found = category.sites.find((origItem) => origItem.siteID === item);
			if (!found) {
				added.push(item);
			}
		});

		setNewAssociations(added);
		setDeleteAssociations(deleted);
	};

	const handleSubmit = async (values) => {
		// setConfirmLoading(true);

		const editCategoryBody = {
			name: values.name,
			description: values.description,
			typeID: values.typeID
		};

		//Compare old state with new state
		const infoAdded = !_.isEqual(editCategoryBody, {
			name: oldState.current.name,
			description: oldState.current.description,
			typeID: oldState.current.typeID
		});

		//check if have added sites
		const sitesAdded = !_.isEqual(values.siteID, oldState.current.siteID);
		const fieldsAdded = !_.isEqual(values.fields, oldState.current.fields);

		try {
			if (infoAdded) {
				await SuperFetch.patch(`/deviceCategories/${category.categoryID}`, editCategoryBody);
			} else if (sitesAdded) {
				// added associated sites from the category
				if (newAssociations.length > 0) {
					await SuperFetch.patch(`/deviceCategories/${category.categoryID}/associations`, {
						siteIDs: newAssociations
					});
				} else if (deleteAssociations.length > 0) {
					// remove associated sites from the category
					await SuperFetch.delete(`/deviceCategories/${category.categoryID}/associations`, {
						siteIDs: deleteAssociations
					});
				}
			} else if (fieldsAdded) {
				const createFieldBody = {
					fields: values.fields
				};
				await SuperFetch.post(`/deviceCategories/${category.categoryID}/fields`, createFieldBody);
			}

			dispatch(
				categoriesActions.editCategory({
					...category,
					name: values.name,
					description: values.description,
					typeID: values.typeID,
					fields: values.fields,
					sites: category.sites.filter((item) => values.siteID.includes(item.siteID))
				})
			);
			Notification('success', 'Category updated successfully');
			setConfirmLoading(false);
			handleCancel();
		} catch (error) {
			setConfirmLoading(false);
			Notification('error', 'There is an Error! We are mending the problem, try again soon!.');
		}
	};

	return (
		<div>
			{children ? children : <Button onClick={() => setVisible(true)}>Edit Category</Button>}
			<Modal
				title='Edit Category'
				open={visible}
				onCancel={handleCancel}
				width={650}
				destroyOnClose={true}
				footer={[
					<Flex justify='center' gap={10}>
						<Button key='reset' onClick={handleCancel}>
							Cancel
						</Button>
						<Button
							loading={confirmLoading}
							form={form}
							key='submit'
							htmlType='submit'
							type='primary'
							disabled={disabledEditData}
						>
							Edit Category
						</Button>
					</Flex>
				]}
				styles={{
					body: {
						padding: 10
					}
				}}
			>
				<Divider className='title subtitle' orientation='left'>
					Category Information
				</Divider>
				<Form
					form={form}
					id={form}
					onFinish={handleSubmit}
					layout='vertical'
					onValuesChange={(props, values) => {
						const res = _.isEqual(oldState.current, values);
						setDisabledEditData(res);
					}}
				>
					<Row gutter={20}>
						<Col span={12}>
							<Form.Item name='name' label='Name' rules={fieldsValidator.Name}>
								<Input />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item name='siteID' label='Site'>
								<Select
									mode='multiple'
									placeholder={'Select Site'}
									onChange={(e) => onChangSelect(e)}
									options={sitesData.map((site) => {
										return {
											label: site.name,
											value: site.siteID
										};
									})}
								/>
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item name='typeID' label='Type' rules={fieldsValidator.Required}>
								<Select
									placeholder={'Select Type'}
									options={categoryTypes.map((type) => {
										return {
											label: type.name,
											value: type.typeID
										};
									})}
								/>
							</Form.Item>
						</Col>
						<Col span={24}>
							<Form.Item name='description' label='Description' rules={fieldsValidator.Description}>
								<TextArea
									style={{ color: colorTextDescription }}
									allowClear
									autoSize={{ minRows: 2, maxRows: 6 }}
								/>
							</Form.Item>
						</Col>
					</Row>
					<Divider className='title subtitle' orientation='left'>
						Category Custom Fields
					</Divider>
					<Form.List name='fields'>
						{(fields, { add, remove }) => (
							<Flex vertical>
								{fields.map(({ key, name, ...restField }) => {
									const disabled = name < category.fields.length;
									return (
										<Row key={key} style={{ display: 'flex', marginBottom: 15 }} gutter={10}>
											<Col span={14}>
												<Form.Item
													{...restField}
													name={[name, 'title']}
													rules={fieldsValidator.Name}
													style={{ marginBottom: 0 }}
												>
													<Input disabled={disabled} placeholder='Field Name' />
												</Form.Item>
											</Col>
											<Col span={8}>
												<Form.Item
													{...restField}
													name={[name, 'fieldTypeID']}
													rules={fieldsValidator.Required}
													style={{ marginBottom: 0 }}
												>
													<Select
														placeholder={'Type'}
														disabled={disabled}
														options={fieldTypes.map((type) => {
															return {
																label: type.name,
																value: type.fieldTypeID
															};
														})}
													/>
												</Form.Item>
											</Col>
											{!disabled && (
												<Col span={2} style={{ alignItems: 'center' }}>
													<Form.Item {...restField} style={{ marginBottom: 0 }}>
														<MinusCircleOutlined onClick={() => remove(name)} />
													</Form.Item>
												</Col>
											)}
										</Row>
									);
								})}
								<Form.Item>
									<Button
										type='dashed'
										onClick={() =>
											add({
												fieldTypeID: fieldTypes[0] ? fieldTypes[0].fieldTypeID : null
											})
										}
										block
										icon={<PlusOutlined />}
									>
										Add Custom Field
									</Button>
								</Form.Item>
							</Flex>
						)}
					</Form.List>
				</Form>
			</Modal>
		</div>
	);
};
export default EditCategoryModal;
